'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';

import { useSettings } from '@src/hooks';
import { useAuth } from '@src/context/common';
import api from '@src/api/clients/default';
import paths from '@src/paths';

import config, { filterNavItemsForUser } from '../config';
import { ClientPreviewBanner, ClientViewProvider, useClientView } from '../ClientViewSwitcher';
import Sidebar from './Sidebar';
import useForecastNavEnabled from '../useForecastNavEnabled';
import Navbar from './Navbar';
import AgentChatDock from '@src/components/agent-chat/AgentChatDock';

const normalizeSlug = (value = '') =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function VerticalLayoutInner({ children }) {
  const { settings } = useSettings();
  const { state } = useAuth();
  const user = state?.user;
  const { viewAsClient } = useClientView();
  const [parentScopeClients, setParentScopeClients] = React.useState([]);
  const [parentDashboards, setParentDashboards] = React.useState([]);
  const forecastEnabled = useForecastNavEnabled(user, viewAsClient?.id || null);

  React.useEffect(() => {
    const isStaff = Boolean(user?.is_staff || user?.is_superuser);
    if (!isStaff) {
      setParentDashboards([]);
      return undefined;
    }
    let active = true;
    const collected = new Map();
    const run = async () => {
      try {
        let url = 'dashboard/clients/?fields=parent_company,parent_company_name&page_size=200&active=true';
        let fetches = 0;
        while (url && fetches < 20 && active) {
          // eslint-disable-next-line no-await-in-loop
          const resp = await api.request({ url, method: 'GET' });
          const results = resp?.data?.results || resp?.data || [];
          results.forEach((c) => {
            if (c.parent_company && c.parent_company_name) {
              collected.set(String(c.parent_company), c.parent_company_name);
            }
          });
          url = resp?.data?.next || null;
          fetches += 1;
        }
        if (!active) return;
        const sorted = Array.from(collected.entries())
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setParentDashboards(sorted);
      } catch {
        if (active) setParentDashboards([]);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [user]);

  React.useEffect(() => {
    let active = true;

    const memberships = Array.isArray(user?.client_memberships)
      ? user.client_memberships
      : [];
    const isClientMember = !user?.is_staff && !user?.is_superuser && memberships.length > 0;
    const primary = isClientMember ? memberships[0] || {} : null;
    const isParentScopeMembership = primary && !primary?.client_id && Boolean(
      primary?.parent_company_id
    );

    if (!isParentScopeMembership) {
      setParentScopeClients([]);
      return () => {
        active = false;
      };
    }

    api
      .request({
        url: 'dashboard/clients/?fields=id,name,slug,status,parent_company,parent_company_name&page_size=500',
        method: 'GET',
      })
      .then((resp) => {
        if (!active) {
          return;
        }
        const rows = Array.isArray(resp?.data?.results)
          ? resp.data.results
          : Array.isArray(resp?.data)
            ? resp.data
            : [];
        const parentId = Number(primary?.parent_company_id);
        const filtered = rows.filter((row = {}) => Number(row.parent_company) === parentId);
        setParentScopeClients(filtered);
      })
      .catch(() => {
        if (active) {
          setParentScopeClients([]);
        }
      });

    return () => {
      active = false;
    };
  }, [user]);

  const navItems = React.useMemo(() => {
    // When staff is viewing as a client, build a fake user for nav filtering
    if (viewAsClient && (user?.is_staff || user?.is_superuser)) {
      const fakeUser = {
        ...user,
        is_staff: false,
        is_superuser: false,
        client_memberships: [
          {
            client_id: viewAsClient.id,
            client_name: viewAsClient.name,
            client_slug: viewAsClient.slug || viewAsClient.name,
            role: 'admin',
            ad_performance_enabled: viewAsClient.ad_performance_enabled !== false,
            creative_review_enabled: viewAsClient.creative_review_enabled === true,
            ...(viewAsClient.membership || {}),
          },
        ],
      };
      return filterNavItemsForUser(config.navItems, fakeUser, { parentScopeClients: [], forecastEnabled });
    }

    const base = filterNavItemsForUser(config.navItems, user, { parentScopeClients, forecastEnabled });

    const isStaff = Boolean(user?.is_staff || user?.is_superuser);
    if (!isStaff || parentDashboards.length === 0) {
      return base;
    }

    const parentDashItem = {
      key: 'parentDashboards',
      title: 'Parent Dashboards',
      icon: 'users',
      items: parentDashboards.map((p) => ({
        key: `parentDashboard-${p.id}`,
        title: p.name,
        href: paths.clientDashboard(normalizeSlug(p.name)),
      })),
    };

    return base.map((group) => {
      if (group.key !== 'performance') return group;
      return { ...group, items: [...group.items, parentDashItem] };
    });
  }, [parentScopeClients, user, viewAsClient, parentDashboards, forecastEnabled]);

  const isStaff = Boolean(user?.is_staff || user?.is_superuser);

  return (
    <React.Fragment>
      <GlobalStyles
        styles={{
          body: {
            '--MainNav-height': '56px',
            '--MainNav-zIndex': 1000,
            '--SideNav-width': '280px',
            '--SideNav-zIndex': 1100,
            '--MobileNav-width': '320px',
            '--MobileNav-zIndex': 1100,
          },
        }}
      />
      <Box
        sx={{
          bgcolor: 'var(--mui-palette-background-default)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          minHeight: '100%',
        }}
      >
        <Sidebar color={settings.navColor} items={navItems} />
        <Box sx={{ display: 'flex', flex: '1 1 auto', flexDirection: 'column', pl: { lg: 'var(--SideNav-width)' } }}>
          <Navbar items={navItems} />
          <ClientPreviewBanner />
          <Box
            component="main"
            sx={{
              '--Content-margin': '0 auto',
              '--Content-maxWidth': 'var(--maxWidth-xl)',
              '--Content-paddingX': '24px',
              '--Content-paddingY': { xs: '24px', lg: '64px' },
              '--Content-padding': 'var(--Content-paddingY) var(--Content-paddingX)',
              '--Content-width': '100%',
              display: 'flex',
              flex: '1 1 auto',
              flexDirection: 'column',
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
      {user ? <AgentChatDock isStaff={Boolean(user?.is_staff || user?.is_superuser)} /> : null}
    </React.Fragment>
  );
}

export default function VerticalLayout({ children }) {
  return (
    <ClientViewProvider>
      <VerticalLayoutInner>{children}</VerticalLayoutInner>
    </ClientViewProvider>
  );
}
